/**
 * useDashboard — fetches KPI data for all modules a user has access to.
 * Module access is derived from user.accessible_modules (explicit overrides)
 * or the role-based defaults stored in ROLE_MODULE_MAP.
 * Uses Promise.allSettled so a single failing service never breaks the dashboard.
 */

import { useState, useEffect, useCallback } from 'react'
import {
  timekeepingService,
  projectsService,
  hrService,
  crmService,
  bidWorkupService,
  identityService,
} from '../shared/api'
import { startOfWeekLocal, addDaysLocal, toLocalISODate } from '../utils/dateUtils'
import { useModuleSwitches } from '../contexts/ModuleSwitchContext.jsx'
import { STANDARD_MODULES, isModuleReleased } from '../shared/constants/modules'

// Default module IDs for each system role.
// Custom roles that have no explicit entry fall back to an empty list,
// which means only modules present in user.accessible_modules will show.
const ROLE_MODULE_MAP = {
  master_admin:  ['timekeeping', 'projects', 'hr', 'crm', 'bidworkup', 'documents'],
  admin:         ['timekeeping', 'projects', 'hr', 'crm', 'bidworkup', 'documents'],
  manager:       ['timekeeping', 'projects', 'crm', 'bidworkup', 'documents'],
  superintendent:['timekeeping', 'projects', 'documents'],
  foreman:       ['timekeeping', 'projects', 'bidworkup'],
  field_worker:  ['timekeeping'],
  employee:      ['timekeeping'],
  auditor:       ['projects'],
  vendor:        [],
  client:        ['bidworkup'],
}

/**
 * Returns the list of module IDs a user can access.
 * Exported so Portal.jsx can reuse the same logic for quick-action filtering.
 */
export const getAccessibleModuleIds = (user) => {
  if (!user) return []
  if (user.accessible_modules?.length) return user.accessible_modules
  const role = user.role?.toLowerCase() || ''
  return ROLE_MODULE_MAP[role] ?? []
}

// Returns { from, to } as YYYY-MM-DD strings for the current ISO week (Mon–Sun)
// THE WEEK WINDOW, and why it is no longer `getWeekStart(new Date())` then `toISOString()`.
// That shape was used by every week widget in this app, and the week maths in it was correct:
// local accessors, Monday found properly. What was wrong is that the Date it produced still
// carried the TIME OF DAY, so writing it down with toISOString() slid it forward a day whenever
// the local clock was past 19:00 US Central. The window became Tue-Mon instead of Mon-Sun:
//
//   the week's own Monday DROPS OUT of the total
//   next week's Monday is counted inside it
//
// and the 40-hour overtime threshold is applied to that total. `startOfWeekLocal` returns LOCAL
// MIDNIGHT, so its value is a calendar day rather than an instant, and `toLocalISODate` is the
// only correct way to write a calendar day down. See src/utils/dateUtils.js.
const getWeekBounds = () => {
  const mon = startOfWeekLocal()
  return { from: toLocalISODate(mon), to: toLocalISODate(addDaysLocal(mon, 6)) }
}

export const useDashboard = (user) => {
  const [data, setData]           = useState({})
  const [loading, setLoading]     = useState(true)
  const [lastRefresh, setLastRefresh] = useState(null)

  // A module switched off for this company (Super Admin's module switches) has no tile and is not
  // asked for figures: its service would only refuse. Same rule as the nav and the portal tiles.
  const { switches } = useModuleSwitches()
  const released = (ids) => ids.filter((id) => isModuleReleased(id, switches))

  // Stable key so useCallback only re-creates when the user's identity - or which STANDARD modules are
  // switched off - changes. Not the whole switches object: it goes {} -> {capex: true} on first load when
  // a held module is on, and that re-ran every figure (review APP-7) though no figure depends on it.
  const switchedOff = STANDARD_MODULES.filter((id) => switches?.[id] === false).join(',')
  const userKey = `${user?.id}_${user?.role}_${user?.accessible_modules?.join(',')}_${switchedOff}`

  const fetchData = useCallback(async () => {
    if (!user) { setLoading(false); return }
    setLoading(true)

    const ids     = released(getAccessibleModuleIds(user))
    const isAdmin = ['admin', 'master_admin'].includes(user.role?.toLowerCase())

    const tasks = []

    if (ids.includes('timekeeping')) {
      const { from, to } = getWeekBounds()
      tasks.push(
        timekeepingService
          .getSummary({ date_from: from, date_to: to })
          .then(d  => ['timekeeping', d])
          .catch(() => ['timekeeping', 'OFFLINE'])
      )
    }

    if (ids.includes('projects')) {
      tasks.push(
        projectsService
          .getStatistics()
          .then(d  => ['projects', d])
          .catch(() => ['projects', 'OFFLINE'])
      )
    }

    if (ids.includes('hr')) {
      tasks.push(
        hrService
          .getStatistics()
          .then(d  => ['hr', d])
          .catch(() => ['hr', 'OFFLINE'])
      )
    }

    if (ids.includes('crm')) {
      tasks.push(
        crmService
          .getClientStatistics()
          .then(d  => ['crm', d])
          .catch(() => ['crm', 'OFFLINE'])
      )
    }

    // THE BID TILE, and why it asks a permission rather than naming roles.
    //
    // It used to be drawn only for `bidworkup` and to choose its endpoint from a role list - foreman,
    // bid_requestor, client. Measured against the eleven staging logins (2.99.49): none of the roles
    // on that list holds `bidworkup` (foreman and client hold `bid_request`, a different module, and
    // bid_requestor is not a role at all), so the request-stats branch never ran and the foreman -
    // the person who RAISES bid requests - had no bid tile. And a role handed `bidworkup` without
    // `bid:view_bids` fell to `/bids/`, was refused, and showed the tile as "Service offline".
    //
    // Now: either module draws the tile. `bid:view_bids` - the permission `/bids/` checks, with
    // admin/master_admin passing every check - gets the pipeline; everyone else gets the request
    // counts, which `/bid-requests/stats` serves to any signed-in user (their own requests, or all
    // of them for a holder of `bid:view_bid_requests`). A refusal is REFUSED, not OFFLINE: a service
    // that answered "no" is not a service that is down.
    if (ids.includes('bidworkup') || ids.includes('bid_request')) {
      const canSeeBids = isAdmin || (user.permissions || []).includes('bid:view_bids')
      tasks.push(
        (canSeeBids
          ? bidWorkupService.getBids().then(d => ['bidworkup', d])
          : bidWorkupService.getBidRequestStats().then(d => ['bidworkup', { _type: 'requests', ...d }])
        ).catch(err => ['bidworkup', err?.response?.status === 403 ? 'REFUSED' : 'OFFLINE'])
      )
    }

    if (isAdmin) {
      tasks.push(
        identityService
          .getUsers()
          .then(d  => ['users', d])
          .catch(() => ['users', 'OFFLINE'])
      )
    }

    // Clear existing data so skeletons appear on refresh
    setData({})
    setLoading(true)

    // Execute all tasks independently
    Promise.all(tasks.map(task => 
      task.then(([key, val]) => {
        setData(prev => ({ ...prev, [key]: val }))
      })
    )).finally(() => {
      setLastRefresh(new Date())
      setLoading(false)
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userKey])

  useEffect(() => { fetchData() }, [fetchData])

  const isAdmin   = ['admin', 'master_admin'].includes(user?.role?.toLowerCase())
  const moduleIds = released(getAccessibleModuleIds(user))

  return { data, loading, moduleIds, isAdmin, refresh: fetchData, lastRefresh }
}
